/**
 * phone-e164 — مصدر الحقيقة الوحيد لتطبيع أرقام الهاتف إلى صيغة E.164.
 *
 * السبب: شوبيفاي يمرّر الرقم كما كتبه العميل في checkout (مثل `0508877701`
 * أو `508877701`)، وTwilio/WhatsApp يرفض أيّ رقم بلا `+` ومفتاح دولة.
 *
 * قاعدة صارمة: لا نخمّن الدولة من الرقم نفسه — نستخدم كود دولة الطلب/السوق.
 * إن كان الطول غير منطقيّ نعيد null (لا نرسل) بدل تخريب الرقم.
 */

/** Arabic/Persian decimal digits, without guessing a country. */
export function latinDigits(raw: string): string {
  return raw.replace(/[٠-٩۰-۹]/g, c => String(c.charCodeAt(0) - (c >= "۰" ? 0x6f0 : 0x660)));
}

/** مفتاح الاتّصال + الأطوال الوطنيّة المقبولة لكلّ دولة ندعمها. */
const COUNTRY_RULES: Record<string, { dial: string; nationalLengths: number[] }> = {
  AE: { dial: "971", nationalLengths: [9] },
  SA: { dial: "966", nationalLengths: [9] },
  OM: { dial: "968", nationalLengths: [8] },
  IQ: { dial: "964", nationalLengths: [10] },
  KW: { dial: "965", nationalLengths: [8] },
  QA: { dial: "974", nationalLengths: [8] },
  BH: { dial: "973", nationalLengths: [8] },
  US: { dial: "1", nationalLengths: [10] },
};

export interface NormalizeResult {
  phone: string | null;
  reason?: "empty" | "no_country" | "bad_length" | "unsupported_country";
}

/**
 * يحوّل رقماً خاماً إلى E.164 اعتماداً على كود دولة الطلب.
 * - `+971501234567` → كما هو (بعد تنظيف الفواصل).
 * - `00971501234567` → `+971501234567`.
 * - `0501234567` + AE → `+971501234567` (نحذف الصفر الوطنيّ فقط).
 * - `501234567` + AE → `+971501234567` (لا نحذف الـ 5 لأنّه ليس صفر البادئة).
 * - `971501234567` + AE → `+971501234567`.
 */
export function normalizeToE164(
  raw: string | null | undefined,
  countryCode: string | null | undefined,
): NormalizeResult {
  if (!raw) return { phone: null, reason: "empty" };

  let s = latinDigits(String(raw)).replace(/[^\d+]/g, "");
  if (!s) return { phone: null, reason: "empty" };

  // صيغة دوليّة صريحة
  if (s.startsWith("+")) {
    const digits = s.slice(1).replace(/\D/g, "");
    // حالة خاصّة: `+0501234567` (صفر وطنيّ خلف +) — نعالجها كرقم وطنيّ
    if (!digits.startsWith("0")) {
      return digits.length >= 8 && digits.length <= 15
        ? { phone: `+${digits}` }
        : { phone: null, reason: "bad_length" };
    }
    s = digits;
  }

  if (s.startsWith("00")) {
    const digits = s.slice(2);
    return digits.length >= 8 && digits.length <= 15
      ? { phone: `+${digits}` }
      : { phone: null, reason: "bad_length" };
  }

  const cc = (countryCode || "").toUpperCase();
  if (!cc) return { phone: null, reason: "no_country" };
  const rule = COUNTRY_RULES[cc];
  if (!rule) return { phone: null, reason: "unsupported_country" };

  // الرقم يبدأ بمفتاح الدولة أصلاً (بلا + ولا 00)
  if (s.startsWith(rule.dial)) {
    const national = s.slice(rule.dial.length);
    if (rule.nationalLengths.includes(national.length)) {
      return { phone: `+${rule.dial}${national}` };
    }
  }

  // نحذف أصفار البادئة الوطنيّة فقط (لا نحذف أيّ رقم آخر)
  const national = s.replace(/^0+/, "");
  if (!rule.nationalLengths.includes(national.length)) {
    return { phone: null, reason: "bad_length" };
  }
  return { phone: `+${rule.dial}${national}` };
}

/** واجهة مبسّطة تعيد الرقم أو null. */
export function toE164(
  raw: string | null | undefined,
  countryCode: string | null | undefined,
): string | null {
  return normalizeToE164(raw, countryCode).phone;
}

/**
 * تطبيع بلا تخمين دولة: يقبل فقط الصيغ الدوليّة الواضحة:
 * - `+971501234567` / `00971501234567`
 * - `971501234567` (أرقام صافية تبدأ بمفتاح دولة مدعومة وبطول وطنيّ مطابق)
 * يرفض الأرقام الوطنيّة المبهمة (`0501234567`) لأنّ دولتها غير قابلة للجزم.
 */
export function toE164International(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const s = latinDigits(String(raw)).replace(/[^\d+]/g, "");
  if (!s) return null;

  if (s.startsWith("+") || s.startsWith("00")) {
    return normalizeToE164(s, null).phone;
  }

  if (s.startsWith("0")) return null; // رقم وطنيّ مبهم — لا نخمّن دولته
  if (!/^\d+$/.test(s)) return null;

  for (const rule of Object.values(COUNTRY_RULES)) {
    if (!s.startsWith(rule.dial)) continue;
    const national = s.slice(rule.dial.length);
    if (rule.nationalLengths.includes(national.length)) {
      return `+${s}`;
    }
  }
  return null;
}

